import type {
  RatingWithColor,
  RatingWithColorAndLikelihoodImpact,
} from '@risksmart-app/components/src/hooks/types';
import { useCallback, useMemo } from 'react';

import type { ConfigType } from '@/hooks/queries/risk-assessment-result-config/types';
import type { RiskAssessmentResultConfig } from '@/hooks/queries/risk-assessment-result-config/useGetLatestRiskAssessmentResultConfig';
import { useGetLatestRiskAssessmentResultConfig } from '@/hooks/queries/risk-assessment-result-config/useGetLatestRiskAssessmentResultConfig';
import { useGetRiskAssessmentResultConfigByIds } from '@/hooks/queries/risk-assessment-result-config/useGetRiskAssessmentResultConfigByIds';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

export interface MatrixCell {
  label: string;
  value: number;
  color: string;
  likelihood: number;
  impact: number;
}

const toRatingOptions = (
  matrix: RiskAssessmentResultConfig['matrix']
): RatingWithColorAndLikelihoodImpact<number>[] =>
  matrix.map((entry) => ({
    label: entry.title,
    value: entry.value,
    color: entry.color,
    likelihoodImpact: [{ likelihood: entry.likelihood, impact: entry.impact }],
    range: [0, 0] as readonly [number, number],
  }));

const toLikelihoodImpactOptions = (
  ratings: { title: string; value: number; color: string }[]
): RatingWithColor<number>[] =>
  ratings.map((r) => ({
    label: r.title,
    value: r.value,
    color: r.color,
  }));

const matrixKey = (likelihood: number, impact: number) =>
  `${likelihood}:${impact}`;

const buildMatrixLookup = (
  matrix: RiskAssessmentResultConfig['matrix']
): Map<string, MatrixCell> => {
  const map = new Map<string, MatrixCell>();
  for (const entry of matrix) {
    map.set(matrixKey(entry.likelihood, entry.impact), {
      label: entry.title,
      value: entry.value,
      color: entry.color,
      likelihood: entry.likelihood,
      impact: entry.impact,
    });
  }

  return map;
};

const buildValueLookup = (
  ratings: { title: string; value: number; color: string }[]
): Map<number, RatingWithColor<number>> => {
  const map = new Map<number, RatingWithColor<number>>();
  for (const r of ratings) {
    map.set(r.value, { label: r.title, value: r.value, color: r.color });
  }

  return map;
};

interface ConfigLookups {
  matrixLookup: Map<string, MatrixCell>;
  likelihoodByValue: Map<number, RatingWithColor<number>>;
  impactByValue: Map<number, RatingWithColor<number>>;
}

const buildConfigLookups = (
  config: RiskAssessmentResultConfig
): ConfigLookups => ({
  matrixLookup: buildMatrixLookup(config.matrix),
  likelihoodByValue: buildValueLookup(config.likelihood.ratings),
  impactByValue: buildValueLookup(config.impact.ratings),
});

export const useScoringSettings = (options: {
  configType: ConfigType;
  configIds?: string[];
  skip?: boolean;
}) => {
  const scoringSettingsDataEnabled = useIsFeatureFlagEnabled(
    'scoring_settings_data'
  );
  const skipQueries = !scoringSettingsDataEnabled || options.skip === true;
  const {
    config: rawConfig,
    loading,
    id: latestConfigId,
  } = useGetLatestRiskAssessmentResultConfig({
    type: options.configType,
    skip: skipQueries,
  });
  const latestConfig = scoringSettingsDataEnabled ? rawConfig : undefined;
  const hasScoringSettings = !!latestConfig;

  // The latest config is already seeded into lookupCache below, so exclude it
  // from the by-id fetch (which is cache-and-network and would otherwise
  // refetch it). Only historical config versions need fetching.
  const historicalConfigIds = useMemo(
    () => (options.configIds ?? []).filter((id) => id !== latestConfigId),
    [options.configIds, latestConfigId]
  );

  const { configById } = useGetRiskAssessmentResultConfigByIds(
    historicalConfigIds,
    { skip: skipQueries }
  );

  const latestLookups = useMemo(
    () =>
      latestConfig
        ? buildConfigLookups(latestConfig)
        : {
            matrixLookup: new Map<string, MatrixCell>(),
            likelihoodByValue: new Map<number, RatingWithColor<number>>(),
            impactByValue: new Map<number, RatingWithColor<number>>(),
          },
    [latestConfig]
  );

  const lookupCache = useMemo(() => {
    const cache = new Map<string, ConfigLookups>();
    if (latestConfigId && latestConfig) {
      cache.set(latestConfigId, latestLookups);
    }
    configById.forEach((c, id) => {
      cache.set(id, buildConfigLookups(c));
    });

    return cache;
  }, [configById, latestConfigId, latestConfig, latestLookups]);

  const getLookupsForConfig = useCallback(
    (configId: string | null | undefined): ConfigLookups | undefined => {
      if (configId) {
        return lookupCache.get(configId);
      }

      return latestLookups;
    },
    [lookupCache, latestLookups]
  );

  const getRatingByLikelihoodAndImpact = useCallback(
    (
      likelihood: number,
      impact: number,
      configId?: string | null
    ): MatrixCell | undefined =>
      getLookupsForConfig(configId)?.matrixLookup.get(
        matrixKey(likelihood, impact)
      ),
    [getLookupsForConfig]
  );

  const getLikelihoodByValue = useCallback(
    (
      value: number,
      configId?: string | null
    ): RatingWithColor<number> | undefined =>
      getLookupsForConfig(configId)?.likelihoodByValue.get(value),
    [getLookupsForConfig]
  );

  const getImpactByValue = useCallback(
    (
      value: number,
      configId?: string | null
    ): RatingWithColor<number> | undefined =>
      getLookupsForConfig(configId)?.impactByValue.get(value),
    [getLookupsForConfig]
  );

  const riskRatingOptions = useMemo<RatingWithColor<number>[]>(() => {
    if (!latestConfig) {
      return [];
    }
    const seen = new Map<number, RatingWithColor<number>>();
    for (const entry of latestConfig.matrix) {
      if (!seen.has(entry.value)) {
        seen.set(entry.value, {
          label: entry.title,
          value: entry.value,
          color: entry.color,
        });
      }
    }

    return Array.from(seen.values()).sort((a, b) => a.value - b.value);
  }, [latestConfig]);

  const impactCategories = latestConfig?.impact.categories ?? [];
  const hasMultipleImpacts = hasScoringSettings && impactCategories.length >= 2;

  return {
    hasScoringSettings,
    hasMultipleImpacts,
    loading,
    latestConfigId,
    config: latestConfig,
    matrixOptions: latestConfig ? toRatingOptions(latestConfig.matrix) : [],
    likelihoodOptions: latestConfig
      ? toLikelihoodImpactOptions(latestConfig.likelihood.ratings)
      : [],
    impactOptions: latestConfig
      ? toLikelihoodImpactOptions(latestConfig.impact.ratings)
      : [],
    riskRatingOptions,
    impactCategories,
    getRatingByLikelihoodAndImpact,
    getLikelihoodByValue,
    getImpactByValue,
  };
};
